import {
  DOORS,
  FLOORS,
  FURNITURE,
  WALLS,
  WORLD,
  ZONES,
  type Door,
  type FloorArea,
  type Furniture,
  type Rect,
  type Zone,
} from './map';
import { pieceRotation, visualBounds } from './rotation';

/**
 * Digital Office — static scene renderer.
 *
 * All functions draw in WORLD space (the canvas has the camera transform
 * applied already) and cull against `view`, the visible world rectangle,
 * so panning around the big map stays cheap. Style is deliberately soft and
 * modern — rounded shapes, flat fills with a single shade step, gentle
 * contact shadows — rather than pixel art.
 */

export const PALETTE = {
  grass: '#CBD8B2',
  grassDark: '#BFCFA3',
  pavement: '#E2DFD7',
  pavementEdge: '#CFCBC1',
  concrete: '#D9D7D1',
  concreteLine: '#CECBC4',
  carpet: '#BFC9D0',
  wood: '#D8BD95',
  woodLine: '#C9AC84',
  rug: '#B86B5A',
  rugEdge: '#9E5747',
  wall: '#F7F5EF',
  wallEdge: '#6B716D',
  wallShadow: 'rgba(27, 46, 29, 0.20)',
  label: 'rgba(27, 46, 29, 0.30)',
  rack: '#4E545A',
  rackEdge: '#3A3F44',
  rackShelf: 'rgba(255,255,255,0.10)',
  boxes: ['#C5DD85', '#E8907A', '#AAD8FA', '#EFEADF', '#D9B16A'],
  deskTop: '#F1EEE6',
  deskEdge: '#CFC9BC',
  monitor: '#2E3338',
  screen: '#AAD8FA',
  plantPot: '#A8705A',
  leaf: ['#5F8A4E', '#7BA35E', '#4B6E3F'],
  couch: '#4B5E43',
  cushion: '#5E7355',
  machine: '#6B7177',
  machineEdge: '#50555A',
  moss: '#C5DD85',
  humus: '#1B2E1D',
  chair: '#3F4752',
  dock: '#F0C75E',
  mat: '#8E8B84',
} as const;

export interface ViewRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function inView(r: Rect, view: ViewRect, pad = 8): boolean {
  return (
    r.x < view.x + view.w + pad &&
    r.x + r.w > view.x - pad &&
    r.y < view.y + view.h + pad &&
    r.y + r.h > view.y - pad
  );
}

export function withRotation(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  deg: number,
  draw: () => void,
) {
  if (!deg) {
    draw();
    return;
  }
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((deg * Math.PI) / 180);
  ctx.translate(-cx, -cy);
  draw();
  ctx.restore();
}

export function withRotationAt(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  deg: number,
  draw: () => void,
) {
  if (!deg) {
    draw();
    return;
  }
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((deg * Math.PI) / 180);
  ctx.translate(-cx, -cy);
  draw();
  ctx.restore();
}

export function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

// ───────────────────────────────────────────────────────────────────────────
// Outdoors
// ───────────────────────────────────────────────────────────────────────────

/** Decorative trees / shrubs around the lot. Purely visual, never solid. */
const TREES: Array<{ x: number; y: number; r: number }> = [
  { x: 20, y: 60, r: 9 },
  { x: 18, y: 330, r: 8 },
  { x: 22, y: 560, r: 10 },
  { x: 20, y: 760, r: 8 },
  { x: 24, y: 900, r: 9 },
  { x: 20, y: 1010, r: 8 },
  { x: 582, y: 80, r: 9 },
  { x: 580, y: 480, r: 8 },
  { x: 584, y: 640, r: 10 },
  { x: 582, y: 860, r: 9 },
  { x: 580, y: 1000, r: 8 },
  { x: 120, y: 745, r: 8 },
  { x: 240, y: 755, r: 9 },
  { x: 470, y: 748, r: 8 },
  { x: 100, y: 12, r: 7 },
  { x: 330, y: 12, r: 7 },
  { x: 500, y: 12, r: 8 },
  { x: 520, y: 600, r: 9 },
  { x: 540, y: 520, r: 7 },
];

export function drawOutdoors(ctx: CanvasRenderingContext2D, view: ViewRect) {
  // Grass — flat fill with faint mow-stripes for texture.
  ctx.fillStyle = PALETTE.grass;
  ctx.fillRect(0, 0, WORLD.w, WORLD.h);
  ctx.fillStyle = PALETTE.grassDark;
  const stripe = 48;
  const start = Math.floor(view.y / stripe) * stripe;
  for (let y = start; y < view.y + view.h; y += stripe * 2) {
    ctx.globalAlpha = 0.35;
    ctx.fillRect(0, y, WORLD.w, stripe);
    ctx.globalAlpha = 1;
  }
  // Yard behind the dock doors.
  ctx.fillStyle = PALETTE.pavement;
  ctx.fillRect(561, 280, 39, 130);
  ctx.strokeStyle = PALETTE.pavementEdge;
  ctx.lineWidth = 1;
  ctx.strokeRect(561.5, 280.5, 38, 129);

  // Trees
  for (const t of TREES) {
    if (!inView({ x: t.x - t.r, y: t.y - t.r, w: t.r * 2, h: t.r * 2 }, view)) continue;
    ctx.fillStyle = 'rgba(27,46,29,0.15)';
    ctx.beginPath();
    ctx.ellipse(t.x + 1.5, t.y + 2.5, t.r * 1.05, t.r * 0.9, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = PALETTE.leaf[0];
    ctx.beginPath();
    ctx.arc(t.x, t.y, t.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = PALETTE.leaf[1];
    ctx.beginPath();
    ctx.arc(t.x - t.r * 0.3, t.y - t.r * 0.3, t.r * 0.55, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Floors
// ───────────────────────────────────────────────────────────────────────────

function drawFloor(ctx: CanvasRenderingContext2D, f: FloorArea, view: ViewRect) {
  if (!inView(f, view)) return;
  ctx.save();
  ctx.beginPath();
  ctx.rect(f.x, f.y, f.w, f.h);
  ctx.clip();
  switch (f.kind) {
    case 'concrete': {
      ctx.fillStyle = f.color ?? PALETTE.concrete;
      ctx.fillRect(f.x, f.y, f.w, f.h);
      ctx.strokeStyle = PALETTE.concreteLine;
      ctx.lineWidth = 0.6;
      const g = 40;
      for (let x = f.x + g; x < f.x + f.w; x += g) {
        ctx.beginPath();
        ctx.moveTo(x, Math.max(f.y, view.y));
        ctx.lineTo(x, Math.min(f.y + f.h, view.y + view.h));
        ctx.stroke();
      }
      for (let y = f.y + g; y < f.y + f.h; y += g) {
        ctx.beginPath();
        ctx.moveTo(Math.max(f.x, view.x), y);
        ctx.lineTo(Math.min(f.x + f.w, view.x + view.w), y);
        ctx.stroke();
      }
      break;
    }
    case 'carpet': {
      ctx.fillStyle = f.color ?? PALETTE.carpet;
      ctx.fillRect(f.x, f.y, f.w, f.h);
      // Subtle carpet-tile seams
      ctx.strokeStyle = 'rgba(255,255,255,0.18)';
      ctx.lineWidth = 0.5;
      const g = 24;
      for (let x = f.x + g; x < f.x + f.w; x += g) {
        ctx.beginPath();
        ctx.moveTo(x, f.y);
        ctx.lineTo(x, f.y + f.h);
        ctx.stroke();
      }
      for (let y = f.y + g; y < f.y + f.h; y += g) {
        ctx.beginPath();
        ctx.moveTo(f.x, y);
        ctx.lineTo(f.x + f.w, y);
        ctx.stroke();
      }
      break;
    }
    case 'wood': {
      ctx.fillStyle = f.color ?? PALETTE.wood;
      ctx.fillRect(f.x, f.y, f.w, f.h);
      ctx.strokeStyle = PALETTE.woodLine;
      ctx.lineWidth = 0.6;
      const g = 11;
      for (let y = f.y + g; y < f.y + f.h; y += g) {
        ctx.beginPath();
        ctx.moveTo(f.x, y);
        ctx.lineTo(f.x + f.w, y);
        ctx.stroke();
      }
      break;
    }
    case 'pavement': {
      ctx.fillStyle = f.color ?? PALETTE.pavement;
      ctx.fillRect(f.x, f.y, f.w, f.h);
      ctx.strokeStyle = PALETTE.pavementEdge;
      ctx.lineWidth = 0.8;
      for (let y = f.y + 16; y < f.y + f.h; y += 16) {
        ctx.beginPath();
        ctx.moveTo(f.x, y);
        ctx.lineTo(f.x + f.w, y);
        ctx.stroke();
      }
      break;
    }
    case 'rug': {
      roundRectPath(ctx, f.x, f.y, f.w, f.h, 4);
      ctx.fillStyle = f.color ?? PALETTE.rug;
      ctx.fill();
      ctx.strokeStyle = PALETTE.rugEdge;
      ctx.lineWidth = 1.2;
      roundRectPath(ctx, f.x + 3, f.y + 3, f.w - 6, f.h - 6, 3);
      ctx.stroke();
      break;
    }
  }
  ctx.restore();
}

export function drawFloors(
  ctx: CanvasRenderingContext2D,
  view: ViewRect,
  floors: readonly FloorArea[] = FLOORS,
) {
  for (const f of floors) drawFloor(ctx, f, view);
}

// ───────────────────────────────────────────────────────────────────────────
// Zone labels (floor text, no outlines)
// ───────────────────────────────────────────────────────────────────────────

export function drawZoneLabels(
  ctx: CanvasRenderingContext2D,
  view: ViewRect,
  zoom: number,
  zones: readonly Zone[] = ZONES,
) {
  if (zoom < 0.7) return;
  ctx.save();
  ctx.fillStyle = PALETTE.label;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const z of zones) {
    if (!z.showLabel || !inView(z, view)) continue;
    const big = z.w > 300;
    const size = big ? 11 : 6.5;
    ctx.font = `500 ${size}px Geist, system-ui, sans-serif`;
    const text = z.label.toUpperCase();
    // Big areas: label near the open middle; rooms: centred.
    if (z.id === 'front-office') continue; // room labels already cover it
    const cx = z.labelAt?.x ?? z.x + z.w / 2;
    const cy = z.labelAt?.y ?? (z.id === 'warehouse' ? z.y + z.h * 0.62 : z.y + z.h / 2);
    ctx.fillText(text, cx, cy);
  }
  ctx.restore();
}

// ───────────────────────────────────────────────────────────────────────────
// Walls + doors
// ───────────────────────────────────────────────────────────────────────────

export function drawWalls(
  ctx: CanvasRenderingContext2D,
  view: ViewRect,
  walls: readonly Rect[] = WALLS,
) {
  const visible = walls.filter((w) => inView(visualBounds(w), view));
  // Shadows first so they never sit on top of a neighbouring wall.
  ctx.fillStyle = PALETTE.wallShadow;
  for (const w of visible) {
    withRotation(ctx, w, pieceRotation(w), () => {
      roundRectPath(ctx, w.x + 1.2, w.y + 1.8, w.w, w.h, 1.2);
      ctx.fill();
    });
  }
  ctx.lineWidth = 0.8;
  for (const w of visible) {
    withRotation(ctx, w, pieceRotation(w), () => {
      roundRectPath(ctx, w.x, w.y, w.w, w.h, 1.2);
      ctx.fillStyle = PALETTE.wall;
      ctx.fill();
      ctx.strokeStyle = PALETTE.wallEdge;
      ctx.stroke();
    });
  }
}

function drawDoor(ctx: CanvasRenderingContext2D, d: Door) {
  switch (d.kind) {
    case 'door': {
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      roundRectPath(ctx, d.x, d.y, d.w, d.h, 1);
      ctx.fill();
      ctx.strokeStyle = 'rgba(107,113,109,0.5)';
      ctx.lineWidth = 0.6;
      ctx.setLineDash([2, 2]);
      ctx.stroke();
      ctx.setLineDash([]);
      break;
    }
    case 'entrance': {
      roundRectPath(ctx, d.x, d.y, d.w, d.h, 1.5);
      ctx.fillStyle = PALETTE.mat;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.25)';
      ctx.lineWidth = 0.6;
      ctx.stroke();
      break;
    }
    case 'dock': {
      roundRectPath(ctx, d.x, d.y, d.w, d.h, 1);
      ctx.fillStyle = PALETTE.dock;
      ctx.fill();
      ctx.save();
      ctx.clip();
      ctx.strokeStyle = 'rgba(27,46,29,0.55)';
      ctx.lineWidth = 1.4;
      for (let i = -d.h; i < d.w + d.h; i += 5) {
        ctx.beginPath();
        ctx.moveTo(d.x + i, d.y);
        ctx.lineTo(d.x + i + d.h, d.y + d.h);
        ctx.stroke();
      }
      ctx.restore();
      break;
    }
  }
}

export function drawDoors(
  ctx: CanvasRenderingContext2D,
  view: ViewRect,
  doors: readonly Door[] = DOORS,
) {
  for (const d of doors) {
    if (!inView(visualBounds(d), view)) continue;
    withRotation(ctx, d, pieceRotation(d), () => drawDoor(ctx, d));
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Furniture
// ───────────────────────────────────────────────────────────────────────────

function softShadow(ctx: CanvasRenderingContext2D, r: Rect, radius = 2) {
  ctx.fillStyle = 'rgba(27,46,29,0.18)';
  roundRectPath(ctx, r.x + 1, r.y + 2, r.w, r.h, radius);
  ctx.fill();
}

function drawRack(ctx: CanvasRenderingContext2D, f: Furniture, idx: number) {
  softShadow(ctx, f, 2);
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 2);
  ctx.fillStyle = PALETTE.rack;
  ctx.fill();
  ctx.strokeStyle = PALETTE.rackEdge;
  ctx.lineWidth = 0.8;
  ctx.stroke();
  // Shelf frame: 2 columns × 3 bays
  ctx.strokeStyle = PALETTE.rackShelf;
  ctx.lineWidth = 1;
  const cols = 2;
  const rows = 3;
  for (let c = 1; c < cols; c++) {
    const x = f.x + (f.w / cols) * c;
    ctx.beginPath();
    ctx.moveTo(x, f.y + 1);
    ctx.lineTo(x, f.y + f.h - 1);
    ctx.stroke();
  }
  for (let r = 1; r < rows; r++) {
    const y = f.y + (f.h / rows) * r;
    ctx.beginPath();
    ctx.moveTo(f.x + 1, y);
    ctx.lineTo(f.x + f.w - 1, y);
    ctx.stroke();
  }
  // Boxes / pallets on some bays (deterministic pattern per rack)
  const cw = f.w / cols;
  const rh = f.h / rows;
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      const seed = (idx * 7 + c * 3 + r * 5) % 11;
      if (seed % 3 === 0) continue;
      const color = PALETTE.boxes[(seed + idx) % PALETTE.boxes.length];
      ctx.fillStyle = color;
      roundRectPath(ctx, f.x + c * cw + 2.5, f.y + r * rh + 2.5, cw - 5, rh - 5, 1.2);
      ctx.fill();
      ctx.fillStyle = 'rgba(0,0,0,0.10)';
      ctx.fillRect(f.x + c * cw + 2.5, f.y + r * rh + rh - 5.5, cw - 5, 3);
    }
  }
}

function drawDesk(ctx: CanvasRenderingContext2D, f: Furniture) {
  softShadow(ctx, f, 2.5);
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 2.5);
  ctx.fillStyle = PALETTE.deskTop;
  ctx.fill();
  ctx.strokeStyle = PALETTE.deskEdge;
  ctx.lineWidth = 0.8;
  ctx.stroke();
  // Monitor + keyboard. The piece itself is rotated; this is the 0° pose
  // (chair on the south side, monitor along the north edge).
  const mw = Math.min(14, f.w * 0.4);
  const mx = f.x + f.w / 2 - mw / 2;
  const my = f.y + 2;
  ctx.fillStyle = PALETTE.monitor;
  roundRectPath(ctx, mx, my, mw, 3.5, 1);
  ctx.fill();
  ctx.fillStyle = PALETTE.screen;
  roundRectPath(ctx, mx + 1, my + 0.8, mw - 2, 2, 0.5);
  ctx.fill();
  ctx.fillStyle = '#CFCBC1';
  const ky = f.y + f.h - 6;
  roundRectPath(ctx, f.x + f.w / 2 - 5, ky, 10, 3, 0.8);
  ctx.fill();
  // Mug
  ctx.fillStyle = PALETTE.moss;
  ctx.beginPath();
  ctx.arc(f.x + f.w - 5, f.y + f.h / 2, 1.6, 0, Math.PI * 2);
  ctx.fill();
}

function drawTable(ctx: CanvasRenderingContext2D, f: Furniture) {
  softShadow(ctx, f, 6);
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 6);
  ctx.fillStyle = PALETTE.deskTop;
  ctx.fill();
  ctx.strokeStyle = PALETTE.deskEdge;
  ctx.lineWidth = 0.8;
  ctx.stroke();
  if (f.label === 'Game table') {
    // Ping-pong net
    ctx.strokeStyle = '#6B716D';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(f.x + f.w / 2, f.y + 1);
    ctx.lineTo(f.x + f.w / 2, f.y + f.h - 1);
    ctx.stroke();
    ctx.fillStyle = '#F0C75E';
    ctx.beginPath();
    ctx.arc(f.x + f.w * 0.3, f.y + f.h * 0.4, 1.4, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawPingPong(ctx: CanvasRenderingContext2D, f: Furniture) {
  softShadow(ctx, f, 4);
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 2);
  ctx.fillStyle = '#163528';
  ctx.fill();
  const inset = 2.2;
  roundRectPath(ctx, f.x + inset, f.y + inset, f.w - inset * 2, f.h - inset * 2, 1.2);
  ctx.fillStyle = '#1F7A45';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 0.7;
  const line = 1.3;
  roundRectPath(
    ctx,
    f.x + inset + line,
    f.y + inset + line,
    f.w - (inset + line) * 2,
    f.h - (inset + line) * 2,
    0.6,
  );
  ctx.stroke();

  // Net runs across the short axis so the long sides are the two ends.
  const across = f.h >= f.w;
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = 0.55;
  ctx.beginPath();
  if (across) {
    const y = f.y + f.h / 2;
    ctx.moveTo(f.x + inset + line, y);
    ctx.lineTo(f.x + f.w - inset - line, y);
  } else {
    const x = f.x + f.w / 2;
    ctx.moveTo(x, f.y + inset + line);
    ctx.lineTo(x, f.y + f.h - inset - line);
  }
  ctx.stroke();

  ctx.strokeStyle = '#F4F1EA';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  if (across) {
    const y = f.y + f.h / 2;
    ctx.moveTo(f.x + 0.8, y);
    ctx.lineTo(f.x + f.w - 0.8, y);
    ctx.stroke();
    ctx.strokeStyle = '#24302C';
    ctx.lineWidth = 0.35;
    for (let i = 0; i <= 5; i++) {
      const x = f.x + 2 + ((f.w - 4) * i) / 5;
      ctx.beginPath();
      ctx.moveTo(x, y - 2.1);
      ctx.lineTo(x, y + 2.1);
      ctx.stroke();
    }
  } else {
    const x = f.x + f.w / 2;
    ctx.moveTo(x, f.y + 0.8);
    ctx.lineTo(x, f.y + f.h - 0.8);
    ctx.stroke();
    ctx.strokeStyle = '#24302C';
    ctx.lineWidth = 0.35;
    for (let i = 0; i <= 5; i++) {
      const y = f.y + 2 + ((f.h - 4) * i) / 5;
      ctx.beginPath();
      ctx.moveTo(x - 2.1, y);
      ctx.lineTo(x + 2.1, y);
      ctx.stroke();
    }
  }

  ctx.fillStyle = '#F7F4EC';
  ctx.beginPath();
  ctx.arc(f.x + f.w * 0.32, f.y + f.h * 0.28, 1.35, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#0E415E';
  roundRectPath(ctx, f.x + f.w * 0.62, f.y + f.h * 0.62, 4.2, 2.4, 0.8);
  ctx.fill();
}

function drawCounter(ctx: CanvasRenderingContext2D, f: Furniture) {
  softShadow(ctx, f, 2);
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 2);
  ctx.fillStyle = '#E9E6DF';
  ctx.fill();
  ctx.strokeStyle = PALETTE.deskEdge;
  ctx.lineWidth = 0.8;
  ctx.stroke();
  // Front face shade
  ctx.fillStyle = 'rgba(0,0,0,0.07)';
  ctx.fillRect(f.x + 0.5, f.y + f.h - 4, f.w - 1, 3.5);
  // A few items along the counter
  for (let x = f.x + 8; x < f.x + f.w - 6; x += 22) {
    ctx.fillStyle = PALETTE.boxes[Math.floor(x / 22) % PALETTE.boxes.length];
    roundRectPath(ctx, x, f.y + 4, 8, 5, 1);
    ctx.fill();
  }
}

function drawBench(ctx: CanvasRenderingContext2D, f: Furniture) {
  softShadow(ctx, f, 2);
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 2);
  ctx.fillStyle = '#9C7A55';
  ctx.fill();
  ctx.strokeStyle = '#7A5E41';
  ctx.lineWidth = 0.7;
  for (let y = f.y + 3; y < f.y + f.h - 1; y += 3) {
    ctx.beginPath();
    ctx.moveTo(f.x + 1, y);
    ctx.lineTo(f.x + f.w - 1, y);
    ctx.stroke();
  }
}

function drawPlant(ctx: CanvasRenderingContext2D, f: Furniture) {
  const cx = f.x + f.w / 2;
  const cy = f.y + f.h / 2;
  ctx.fillStyle = 'rgba(27,46,29,0.18)';
  ctx.beginPath();
  ctx.ellipse(cx + 1, cy + 2, f.w * 0.55, f.h * 0.4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = PALETTE.plantPot;
  roundRectPath(ctx, cx - 3.5, cy, 7, 5, 1.5);
  ctx.fill();
  const leaves: Array<[number, number, number, string]> = [
    [0, -2.5, 4.2, PALETTE.leaf[0]],
    [-3, -0.5, 3.2, PALETTE.leaf[1]],
    [3, -0.8, 3.0, PALETTE.leaf[2]],
    [0.5, -5, 2.6, PALETTE.leaf[1]],
  ];
  for (const [dx, dy, r, c] of leaves) {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.arc(cx + dx, cy + dy, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawCouch(ctx: CanvasRenderingContext2D, f: Furniture) {
  softShadow(ctx, f, 4);
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 4);
  ctx.fillStyle = PALETTE.couch;
  ctx.fill();
  // Backrest along the north edge; rotation turns the whole sofa.
  const backH = 5;
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  roundRectPath(ctx, f.x, f.y, f.w, backH, 3);
  ctx.fill();
  // Cushions
  const n = 3;
  const pad = 5;
  const cw = (f.w - pad * 2) / n;
  const cy = f.y + backH + 1;
  const ch = f.h - backH - 3;
  ctx.fillStyle = PALETTE.cushion;
  for (let i = 0; i < n; i++) {
    roundRectPath(ctx, f.x + pad + i * cw + 0.8, cy, cw - 1.6, ch, 2);
    ctx.fill();
  }
  // Arms
  ctx.fillStyle = 'rgba(255,255,255,0.10)';
  roundRectPath(ctx, f.x, f.y, pad - 1, f.h, 2);
  ctx.fill();
  roundRectPath(ctx, f.x + f.w - pad + 1, f.y, pad - 1, f.h, 2);
  ctx.fill();
}

function drawMachine(ctx: CanvasRenderingContext2D, f: Furniture) {
  softShadow(ctx, f, 2);
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 2);
  ctx.fillStyle = PALETTE.machine;
  ctx.fill();
  ctx.strokeStyle = PALETTE.machineEdge;
  ctx.lineWidth = 0.8;
  ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  roundRectPath(ctx, f.x + 3, f.y + 3, f.w - 6, f.h * 0.45, 1.5);
  ctx.fill();
  // Status light + tape roll
  ctx.fillStyle = PALETTE.moss;
  ctx.beginPath();
  ctx.arc(f.x + f.w - 5, f.y + 5, 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#EFEADF';
  roundRectPath(ctx, f.x + 5, f.y + f.h - 9, 14, 5, 1);
  ctx.fill();
  ctx.fillStyle = '#E8907A';
  roundRectPath(ctx, f.x + 22, f.y + f.h - 9, 10, 5, 1);
  ctx.fill();
}

function drawChair(ctx: CanvasRenderingContext2D, f: Furniture) {
  const cx = f.x + f.w / 2;
  const cy = f.y + f.h / 2;
  ctx.fillStyle = 'rgba(27,46,29,0.14)';
  ctx.beginPath();
  ctx.ellipse(cx + 0.6, cy + 1.4, 5.4, 4, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = PALETTE.chair;
  ctx.beginPath();
  ctx.arc(cx, cy, 4.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.14)';
  ctx.beginPath();
  ctx.arc(cx, cy, 3, 0, Math.PI * 2);
  ctx.fill();
  // Backrest arc (chair faces up toward the desk)
  ctx.strokeStyle = PALETTE.chair;
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.arc(cx, cy + 0.5, 5.2, Math.PI * 0.15, Math.PI * 0.85);
  ctx.stroke();
}

function drawCoffee(ctx: CanvasRenderingContext2D, f: Furniture) {
  softShadow(ctx, f, 2);
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 2);
  ctx.fillStyle = '#EFEADF';
  ctx.fill();
  ctx.strokeStyle = PALETTE.deskEdge;
  ctx.lineWidth = 0.8;
  ctx.stroke();
  // Espresso machine
  ctx.fillStyle = '#2A2A30';
  roundRectPath(ctx, f.x + 4, f.y + 2, 10, f.h - 4, 1.5);
  ctx.fill();
  ctx.fillStyle = PALETTE.moss;
  ctx.beginPath();
  ctx.arc(f.x + 9, f.y + 4.5, 1.2, 0, Math.PI * 2);
  ctx.fill();
  // Cups
  for (let x = f.x + 18; x < f.x + f.w - 4; x += 7) {
    ctx.fillStyle = Math.floor(x / 7) % 2 === 0 ? '#FFFFFF' : '#E8907A';
    ctx.beginPath();
    ctx.arc(x, f.y + f.h / 2, 2, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawSquare(ctx: CanvasRenderingContext2D, f: Furniture) {
  softShadow(ctx, f, 2);
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 2);
  ctx.fillStyle = '#6E7B86';
  ctx.fill();
  ctx.strokeStyle = '#4E585F';
  ctx.lineWidth = 0.9;
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.lineWidth = 0.7;
  roundRectPath(ctx, f.x + 2.5, f.y + 2.5, f.w - 5, f.h - 5, 1.4);
  ctx.stroke();
}

function drawCircleShape(ctx: CanvasRenderingContext2D, f: Furniture) {
  const cx = f.x + f.w / 2;
  const cy = f.y + f.h / 2;
  const rx = f.w / 2;
  const ry = f.h / 2;
  ctx.fillStyle = 'rgba(27,46,29,0.18)';
  ctx.beginPath();
  ctx.ellipse(cx + 1, cy + 1.6, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.fillStyle = PALETTE.machine;
  ctx.fill();
  ctx.strokeStyle = PALETTE.machineEdge;
  ctx.lineWidth = 0.9;
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(cx - rx * 0.12, cy - ry * 0.12, rx * 0.62, ry * 0.62, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.14)';
  ctx.fill();
}

function drawWhiteboard(ctx: CanvasRenderingContext2D, f: Furniture) {
  roundRectPath(ctx, f.x, f.y, f.w, f.h, 1);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.strokeStyle = '#9AA0A6';
  ctx.lineWidth = 0.8;
  ctx.stroke();
  ctx.lineWidth = 0.8;
  const colors = ['#E8907A', '#0E415E', '#335C2C'];
  for (let i = 0; i < 3; i++) {
    ctx.strokeStyle = colors[i];
    ctx.beginPath();
    ctx.moveTo(f.x + 4 + i * (f.w / 3), f.y + 1.5);
    ctx.lineTo(f.x + 4 + i * (f.w / 3) + f.w / 4, f.y + f.h - 1.5);
    ctx.stroke();
  }
}

export function drawFurniturePiece(ctx: CanvasRenderingContext2D, f: Furniture, idx: number) {
  const draw = () => {
    switch (f.kind) {
      case 'rack':
        return drawRack(ctx, f, idx);
      case 'desk':
        return drawDesk(ctx, f);
      case 'table':
        return drawTable(ctx, f);
      case 'pingpong':
        return drawPingPong(ctx, f);
      case 'counter':
        return drawCounter(ctx, f);
      case 'bench':
        return drawBench(ctx, f);
      case 'plant':
        return drawPlant(ctx, f);
      case 'couch':
        return drawCouch(ctx, f);
      case 'machine':
        return drawMachine(ctx, f);
      case 'chair':
        return drawChair(ctx, f);
      case 'coffee':
        return drawCoffee(ctx, f);
      case 'whiteboard':
        return drawWhiteboard(ctx, f);
      case 'square':
        return drawSquare(ctx, f);
      case 'circle':
        return drawCircleShape(ctx, f);
      default:
        return;
    }
  };
  withRotation(ctx, f, pieceRotation(f), draw);
}

/** Flat pieces draw before characters (they're floor-level). */
export const FLAT_KINDS: ReadonlySet<Furniture['kind']> = new Set(['chair', 'whiteboard']);

export function drawFlatFurniture(
  ctx: CanvasRenderingContext2D,
  view: ViewRect,
  furniture: readonly Furniture[] = FURNITURE,
) {
  furniture.forEach((f, idx) => {
    if (FLAT_KINDS.has(f.kind) && inView(visualBounds(f), view)) drawFurniturePiece(ctx, f, idx);
  });
}

/** Furniture labels drawn above everything at high zoom. */
export function drawFurnitureLabels(
  ctx: CanvasRenderingContext2D,
  view: ViewRect,
  zoom: number,
  furniture: readonly Furniture[] = FURNITURE,
) {
  if (zoom < 1.4) return;
  ctx.save();
  ctx.font = '500 4.5px Geist, system-ui, sans-serif';
  ctx.fillStyle = 'rgba(27,46,29,0.55)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  for (const f of furniture) {
    const bounds = visualBounds(f);
    if (!f.label || !inView(bounds, view)) continue;
    ctx.fillText(f.label, bounds.x + bounds.w / 2, bounds.y + bounds.h + 2);
  }
  ctx.restore();
}

/** Floating "want to play?" mark drawn above a ping pong table the player is next to. */
export function drawPingPongBeacon(ctx: CanvasRenderingContext2D, spot: Rect, now: number) {
  const cx = spot.x + spot.w / 2;
  const bob = Math.sin(now / 260) * 1.3;
  const y = spot.y - 9 + bob;
  ctx.save();
  ctx.fillStyle = 'rgba(255,255,255,0.96)';
  ctx.strokeStyle = 'rgba(27,46,29,0.28)';
  ctx.lineWidth = 0.6;
  roundRectPath(ctx, cx - 7.2, y - 7.2, 14.4, 14.4, 4);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#1F7A45';
  roundRectPath(ctx, cx - 4.4, y - 2.6, 5.4, 6.4, 1.5);
  ctx.fill();
  ctx.fillStyle = '#C4A574';
  ctx.fillRect(cx - 1.5, y + 3.2, 1.6, 2.4);
  ctx.fillStyle = '#F7F5EF';
  ctx.beginPath();
  ctx.arc(cx + 3.1, y - 2.6, 1.7, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.strokeStyle = 'rgba(31,122,69,0.65)';
  ctx.lineWidth = 1.15;
  ctx.setLineDash([2.2, 1.6]);
  roundRectPath(ctx, spot.x - 1.6, spot.y - 1.6, spot.w + 3.2, spot.h + 3.2, 2.5);
  ctx.stroke();
  ctx.restore();
}

export { inView };
